import React, { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Logo from '../components/Logo';
import Box from '@mui/joy/Box';
import Dropdown from '@mui/joy/Dropdown';
import Link from '@mui/joy/Link';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Typography from '@mui/joy/Typography';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import QuizIcon from '@mui/icons-material/Quiz';
import GradeIcon from '@mui/icons-material/Grade';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { IconButton } from '@mui/joy';
import UserContext from '../context/UserContext';
import { useMobileMenu } from '../context/MobileMenuContext';

const PATH_SEGMENT_META = {
    profile: { label: 'Profile', Icon: AccountCircleIcon },
    'placement-profile': { label: 'Placement Profile', Icon: BusinessCenterIcon },
    'tests-schedule': { label: 'Tests Schedule', Icon: QuizIcon },
    'test-marks': { label: 'Test Marks', Icon: GradeIcon },
    projects: { label: 'Projects', Icon: AssignmentIcon },
    attendance: { label: 'Attendance', Icon: EventAvailableIcon },
    'campus-confirmation': { label: 'Campus Confirmation', Icon: SchoolIcon },
    'sms-history': { label: 'SMS History', Icon: SmsIcon },
    notifications: { label: 'Notifications', Icon: NotificationsActiveIcon },
    'assessment-status': { label: 'Assessment Status', Icon: AssessmentIcon },
    support: { label: 'Support', Icon: SupportAgentIcon }
};

const formatSegmentLabel = (value) => {
    if (/^\d+$/.test(value)) return value;

    return PATH_SEGMENT_META[value]?.label
        || value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
};

const getSegmentIcon = (value) => {
    const SegmentIcon = PATH_SEGMENT_META[value]?.Icon || (/^\d+$/.test(value) ? AssignmentIcon : null);
    if (!SegmentIcon) return null;

    return <SegmentIcon sx={{ fontSize: '1.1rem' }} />;
};

const collapseBreadcrumbsForMobile = (crumbs) => {
    if (crumbs.length <= 1) {
        return crumbs.map((crumb) => ({ ...crumb, showLabel: false }));
    }

    if (crumbs.length === 2) {
        return [
            { ...crumbs[0], showLabel: false },
            { ...crumbs[1], showLabel: true }
        ];
    }

    return [
        { ...crumbs[crumbs.length - 2], showLabel: false },
        { ...crumbs[crumbs.length - 1], showLabel: true }
    ];
};

const BreadcrumbTrail = ({ crumbs, isMobile, trailRef }) => (
    <Box
        ref={trailRef}
        component="nav"
        aria-label="Breadcrumb"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            width: 'max-content',
            maxWidth: '100%',
            minWidth: 0,
            marginLeft: 'auto',
            gap: isMobile ? 0.25 : 0.5,
            fontWeight: 'lg'
        }}
    >
        {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            const showLabel = crumb.showLabel !== false;

            const content = (
                <>
                    {crumb.icon}
                    {showLabel && (
                        <Typography
                            component="span"
                            level="body-md"
                            sx={{
                                ml: crumb.icon ? 0.5 : 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {crumb.label}
                        </Typography>
                    )}
                </>
            );

            const sharedSx = {
                display: 'inline-flex',
                alignItems: 'center',
                minWidth: 0,
                flexShrink: isLast ? 1 : 0,
                color: isLast ? '#ea7a23' : 'inherit',
                textDecoration: 'none',
                '&:hover': { color: '#ea7a23' }
            };

            return (
                <Fragment key={`${crumb.path}-${index}`}>
                    {index > 0 && (
                        <ChevronRightRoundedIcon
                            aria-hidden
                            sx={{ color: '#ea7a23', fontSize: isMobile ? '1rem' : '1.25rem', flexShrink: 0 }}
                        />
                    )}
                    <Box component="span" sx={{ minWidth: 0, display: 'inline-flex', overflow: isLast ? 'hidden' : 'visible' }}>
                        {isLast ? (
                            <Typography aria-label={crumb.ariaLabel || crumb.label} sx={sharedSx}>
                                {content}
                            </Typography>
                        ) : (
                            <Link
                                component={RouterLink}
                                to={crumb.path}
                                aria-label={crumb.ariaLabel || crumb.label}
                                sx={sharedSx}
                            >
                                {content}
                            </Link>
                        )}
                    </Box>
                </Fragment>
            );
        })}
    </Box>
);

const BreadcrumbDropdown = ({ crumbs }) => (
    <Dropdown>
        <MenuButton
            slots={{ root: IconButton }}
            slotProps={{
                root: {
                    variant: 'soft',
                    color: 'warning',
                    size: 'sm',
                    'aria-label': 'Show full breadcrumb path'
                }
            }}
        >
            <KeyboardArrowDownIcon />
        </MenuButton>
        <Menu placement="bottom-end" sx={{ minWidth: 220, maxWidth: 320 }}>
            {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;

                if (isLast) {
                    return (
                        <MenuItem key={`${crumb.path}-${index}`} selected sx={{ color: '#ea7a23', gap: 1 }}>
                            {crumb.icon}
                            <Typography level="body-sm">{crumb.label}</Typography>
                        </MenuItem>
                    );
                }

                return (
                    <MenuItem
                        key={`${crumb.path}-${index}`}
                        component={RouterLink}
                        to={crumb.path}
                        sx={{ gap: 1 }}
                    >
                        {crumb.icon}
                        <Typography level="body-sm">{crumb.label}</Typography>
                    </MenuItem>
                );
            })}
        </Menu>
    </Dropdown>
);

const Header = ({ breadcrumbsData }) => {
    const location = useLocation();
    const { user } = useContext(UserContext);
    const { isMobile, openMenu } = useMobileMenu();
    const showMobileMenuButton = isMobile && Boolean(user);
    const breadcrumbAreaRef = useRef(null);
    const measureTrailRef = useRef(null);
    const [useBreadcrumbDropdown, setUseBreadcrumbDropdown] = useState(false);

    const getBreadcrumbs = useCallback(() => {
        const defaultCrumbs = [{
            path: '/',
            label: 'Home',
            icon: <HomeRoundedIcon sx={{ fontSize: '1.1rem' }} />
        }];

        if (breadcrumbsData && breadcrumbsData.length > 0) {
            return [...defaultCrumbs, ...breadcrumbsData];
        }

        const pathnames = location.pathname.split('/').filter(Boolean);
        if (pathnames.length === 0) return defaultCrumbs;

        return [
            ...defaultCrumbs,
            ...pathnames.map((value, index) => {
                const path = `/${pathnames.slice(0, index + 1).join('/')}`;
                return {
                    path,
                    label: formatSegmentLabel(value),
                    icon: getSegmentIcon(value)
                };
            })
        ];
    }, [breadcrumbsData, location.pathname]);

    const breadcrumbs = getBreadcrumbs();
    const displayCrumbs = isMobile
        ? collapseBreadcrumbsForMobile(breadcrumbs)
        : breadcrumbs.map((crumb) => ({ ...crumb, showLabel: true }));
    const lastCrumb = breadcrumbs[breadcrumbs.length - 1];

    const evaluateBreadcrumbOverflow = useCallback(() => {
        if (breadcrumbs.length <= 1) {
            setUseBreadcrumbDropdown(false);
            return;
        }

        const areaNode = breadcrumbAreaRef.current;
        const trailNode = measureTrailRef.current;
        if (!areaNode || !trailNode) {
            setUseBreadcrumbDropdown(false);
            return;
        }

        setUseBreadcrumbDropdown(trailNode.scrollWidth > areaNode.clientWidth + 1);
    }, [breadcrumbs.length]);

    useLayoutEffect(() => {
        evaluateBreadcrumbOverflow();
    }, [evaluateBreadcrumbOverflow, displayCrumbs, location.pathname]);

    useEffect(() => {
        let timeoutId = null;
        const handleResize = () => {
            window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(evaluateBreadcrumbOverflow, 150);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
        };
    }, [evaluateBreadcrumbOverflow]);

    return (
        <div>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: '6px',
                    px: 2,
                    borderBottom: '2px solid #eb7a24',
                    minWidth: 0
                }}
            >
                {showMobileMenuButton ? (
                    <IconButton
                        variant="soft"
                        color="warning"
                        aria-label="Open menu"
                        onClick={openMenu}
                        sx={{ flexShrink: 0 }}
                    >
                        <MenuIcon />
                    </IconButton>
                ) : (
                    <Logo width={150} height={50} clickable={false} />
                )}

                <Box
                    ref={breadcrumbAreaRef}
                    sx={{
                        position: 'relative',
                        width: '50%',
                        maxWidth: '50%',
                        minWidth: 0,
                        flex: '0 0 50%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        ml: 'auto'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden'
                        }}
                    >
                        {useBreadcrumbDropdown ? (
                            <>
                                <Typography
                                    level="body-md"
                                    sx={{
                                        color: '#ea7a23',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                        flex: 1,
                                        textAlign: 'right'
                                    }}
                                >
                                    {lastCrumb?.label}
                                </Typography>
                                <BreadcrumbDropdown crumbs={breadcrumbs} />
                            </>
                        ) : (
                            <BreadcrumbTrail crumbs={displayCrumbs} isMobile={isMobile} />
                        )}
                    </Box>

                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 0,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            visibility: 'hidden',
                            pointerEvents: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <BreadcrumbTrail
                            crumbs={displayCrumbs}
                            isMobile={isMobile}
                            trailRef={measureTrailRef}
                        />
                    </Box>
                </Box>
            </Box>
        </div>
    );
};

export default Header;
